import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Survey results"`, the **By question** rendering (P14-03; AMENDED 2026-10-03).
 *
 * One card per questionnaire question, drawn as the design's choice card: the question, its count, and
 * one labelled bar per answer. AMENDED 2026-10-03 (design §16.7.2 AS BUILT 2026-10-03): the questions
 * are the survey questionnaire's — statements answered on an agree/disagree scale, the emotion question
 * and the source question — read from `survey_answer`, so the scale-average and open-answer renderings
 * of the round model are gone. The question's own text is shown in the page's language with the
 * instrument's code beside it, because the code is what a research client's tables are keyed on.
 *
 * THE DENOMINATOR IS THE QUESTION'S OWN. Every share arrives from the API as basis points of this
 * question's `answeredCount` — never of the page's conversations, which include people who declined
 * before the first statement. A question nobody has answered yet prints "—", not 0%.
 *
 * THE BARS CARRY NO DATA. Each bar is `aria-hidden`; the count and the share sit beside it as text, with
 * "2 not sure" where the interviewer said it was unsure and "named by 14" where an emotion or a source
 * was named more often than it was the answer.
 *
 * AMENDED 2026-10-11 (ADR 0053): a questionnaire is an ordered list of scale, choice, open and number
 * questions. A number question adds its summary line (average, lowest, highest, how many) above its values,
 * read in numeric order; an open question shows how many answered in their own words, and says the words
 * themselves are read in the respondents (shown only to a person who may read transcripts).
 */
import type { ReactNode } from "react";

import {
  NO_QUESTION_TEXT,
  answerLabel,
  answeredText,
  countText,
  numberSummaryText,
  mentionText,
  orderedOption,
  questionPlaceText,
  questionTextOf,
  shareText,
  unsureText,
} from "./survey-result-copy.js";
import { meterStyle } from "../../meter.js";
import type { SurveyQuestionResultView } from "../../api/gateway.js";

export interface SurveyQuestionPanelProps {
  readonly question: SurveyQuestionResultView;
  /** The language the page words its questions in (`resultLanguageOf`). */
  readonly languageCode: string | null;
}

export function SurveyQuestionPanel({ question, languageCode }: SurveyQuestionPanelProps): ReactNode {
  usePortalTranslation("operations");
  const text = questionTextOf(question.text, languageCode);
  const option = orderedOption(question);
  const numberSummary = numberSummaryText(question);
  return (
    <div className="c-survey-question">
      <div className="c-survey-question-head">
        <p className="c-survey-question-text">
          <span className="c-survey-question-code">{question.instrumentCode}</span>
          {text ?? NO_QUESTION_TEXT()}
        </p>
        <span className="c-survey-question-counts">
          {questionPlaceText(question)} · {answeredText(question.answeredCount)}
        </span>
      </div>

      {numberSummary === null ? null : <p className="c-note">{numberSummary}</p>}
      {question.questionKind === "open" && question.answeredCount > 0 ? (
        <p className="c-note">{portalText("operations", "open_answers_note_ad53a005")}</p>
      ) : null}
      {(question.selectionMax ?? 1) > 1 ? <p className="c-note">{portalText("operations", "multiple_choices_allowed_percentages_are_per_respondent_an_ecd2c3c5")}</p> : null}
      {option.length === 0 ? (
        <p className="c-note">{portalText("operations", "nobody_has_answered_this_question_yet_49ed3068")}</p>
      ) : (
        <div className="c-survey-tally">
          {option.map((one) => {
            const unsure = unsureText(one);
            const named = mentionText(question.questionKind, one);
            return (
              <span className="c-survey-tally-row" key={one.answer}>
                <span className="c-survey-tally-head">
                  <span>
                    {questionTextOf(one.label ?? {}, languageCode) ?? answerLabel(question.questionKind, one.answer)}
                    {unsure === null && named === null ? null : (
                      <span className="c-survey-tally-note">{[unsure, named].filter((each) => each !== null).join(" · ")}</span>
                    )}
                  </span>
                  <span className="c-survey-tally-count">
                    {countText(one.count)} · {shareText(one.shareBasisPoint, question.answeredCount)}
                  </span>
                </span>
                <span className="c-survey-bar c-survey-bar-slim" aria-hidden="true">
                  <span className="c-survey-bar-fill" style={meterStyle(one.shareBasisPoint / 100)} />
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
